<template>
    <tr :class="{ 'unsaved-changes': form.hasUpdated }" v-show="hasUnsavedChanges" @dblclick="toggleEdit">
        <td>
            <input type="checkbox" v-model="isSelected" @input="toggleSelected" @click.stop />
        </td>
        <td>
            <Link :href="productUrl" preserve-scroll class="underline" @click.stop>
                {{ product.id }}
            </Link>
        </td>
        <td>
            <span v-if="product.is_master_product">{{ product.id }}</span>
            <span v-else>{{ product.related_master_product_id }}</span>
        </td>
        <td>
            {{ product.title }}
            <span v-if="product.description">- {{ product.description }}</span>
            <span v-if="product.size">- {{ product.size }}</span>
        </td>
        <td class="text-center">
            <toggle v-model="form.enabled" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.local" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.hot_buy" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.allow_partial_quantity" @click.stop/>
        </td>
        <td class="text-center">
            <toggle v-model="form.allow_oversell" @click.stop/>
        </td>
        <td class="text-center">
            {{ form.stock }}
        </td>
        <td class="text-center">
            {{ form.cost }}
        </td>
        <td class="text-center price-input">
            <jet-input
                v-model.number="price"
                type="number"
                class="text-right"
                step="any"
                @click.stop
            />

            <jet-input-error :message="errors[`${key}.price`]" />
        </td>

        <td v-for="(pricing, index) in form.pricing_levels" :key="pricing.pricing_level_id" class="text-right price-select">
            <price-select
                v-if="hasUnsavedChanges && (editing || isEditingPricingLevel(pricing.pricing_level_id))"
                class="price-select-sm"
                :pricing="pricing"
                @price-level-update="handlePriceLevelUpdate"
                @click.stop
                @dblclick.stop
            />

            <p v-else class="text-center">
                {{ actualPrice(pricing) }}
            </p>

            <div v-if="pricing.pricing_level_title.includes('Retail')" class="price-select-sm">
                <div class="price-select-sm mt-1" v-if="hasUnsavedChanges && (editing || isEditingPricingLevel(pricing.pricing_level_id))">
                    <input
                        v-model="price_2plus"
                        id="price-2plus"
                        type="number"
                        step="any"
                        class="rounded-l rounded-r-none"
                        :disabled="type === '' || type === null || type === 'disabled'"
                        @click.stop
                    />

                    <select class="rounded-r rounded-l-none" v-model="type" @change="modifyPriceTwoPlus">
                        <option value="fixed">$</option>
                        <option value="disabled">X</option>
                    </select>
                </div>
                <p v-else-if="price_2plus > 0" class="text-center">{{ priceToPlusView(price_2plus) }} (2+)</p>
            </div>

            <jet-input-error :message="errors[`${key}.pricing_levels.${index}.modifier_amount`]" />
        </td>

        <td class="text-right">
            <jet-secondary-button type="button" @click.stop="toggleEdit" style="padding-left: 0.25rem; padding-right: 0.25rem; min-width: 24px;">
                <img v-if="editing" src="/images/icons/x-white.svg" width="18" height="18" />
                <img v-else src="/images/icons/edit.svg" width="18" height="18" />
            </jet-secondary-button>
        </td>
    </tr>
</template>

<script>
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import PriceSelect from '@/Pages/Pricing/Partials/PriceSelect';
import { Link } from '@inertiajs/inertia-vue3';
import Toggle from '@vueform/toggle';
import { getCurrentInstance } from "vue";

import '@vueform/toggle/themes/default.css';

export default {
    components: {
        Link,
        PriceSelect,
        JetInput,
        JetInputError,
        JetSecondaryButton,
        Toggle,
    },

    props: ['form', 'product', 'filters', 'categories', 'selected', 'editing', 'editingPriceLevels', 'errors'],

    data() {
        return {
            isSelected: this.selected,
            type: 'disabled',
        }
    },

    computed: {
        price_2plus: {
            get () {
                return this.form.price_2plus;
            },
            set (newValue) {
                this.$emit('productUpdate', {
                    product: this.product,
                    price_2plus: newValue ? Number(newValue) : null,
                    price: this.price ? Number(this.price) : null,
                });
            }
        },

        price: {
            get () {
                return this.form.price;
            },
            set (newValue) {
                this.$emit('productUpdate', {
                    product: this.product,
                    price: newValue ? Number(newValue) : 0,
                    price_2plus: this.price_2plus ? Number(this.price_2plus) : null,
                });
            }
        },

        hasUnsavedChanges () {
            return this.filters.unsaved_only === false || this.form.hasUpdated;
        },

        key () {
            return getCurrentInstance().vnode.key;
        },

        productUrl () {
            return route('products.edit', {
                product: this.product.id,
                return: route('pricing.index', this.filters),
            });
        },
    },


    methods: {
        modifyPriceTwoPlus() {
            if (this.type === 'fixed') {
                return 'Fixed Price';
            } else if (this.type === 'disabled') {
                this.form.price_2plus = null;
                return "Disabled";
            }
        },

        toggleEdit () {
            this.$emit('edit-toggle', {
                editing: !this.editing,
                id: this.form.id,
            });

            // Clearing Highlighted Text from double clicking
            window.getSelection().removeAllRanges();
        },

        handlePriceLevelUpdate (payload) {
            this.$emit('priceLevelUpdate', {
                pricingLevel: payload,
                product: this.product,
            });

            this.setUpdatedAt();
        },

        actualPrice (pricing) {
            if (pricing.modifier_type === 'disabled') {
                return 'X';
            } else if (pricing.price) {
                return pricing.price.toFixed(2)
            }

            return '';
        },

        priceToPlusView (pricing) {
            if (pricing !== null) {
                return pricing.toFixed(2);
            }
        },

        setUpdatedAt () {
            this.form.hasUpdated = true;
        },

        toggleSelected () {
            this.$emit('select-toggle', {
                selected: !this.isSelected,
                id: this.form.id,
            });
        },

        isEditingPricingLevel(id) {
            return this.editingPriceLevels.findIndex(l => l === id) !== -1;
        },
    },

    watch: {
        'form.enabled': 'setUpdatedAt',
        'form.local': 'setUpdatedAt',
        'form.hot_buy': 'setUpdatedAt',
        'form.allow_partial_quantity': 'setUpdatedAt',
        'form.allow_oversell': 'setUpdatedAt',
        'form.price': 'setUpdatedAt',
        'form.price_2plus': 'setUpdatedAt',

        selected (newValue) {
            this.isSelected = newValue;
        }
    },

    mounted () {
         this.type = this.price_2plus > 0 ? 'fixed' : 'disabled';
    }
}
</script>

<style scoped>
input[type="number"] {
    @apply py-0 px-1;
    max-width: 75px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.price-select {
    width: 150px;
}

.price-input {
    width: 100px;
}

.unsaved-changes {
    @apply bg-yellow-100 !important;
}

select {
    @apply pl-2 py-1 pr-8 border border-gray ring-green;
}

.price-select-sm input[type="number"],
.price-select-sm select {
    @apply py-0;
}
</style>
